import "./ContactItem.css";
import { formatDistanceToNow, isValid } from "date-fns";
import { enUS, es } from "date-fns/locale";
import { useContext, useMemo, useState } from "react";
import { useEffect } from "react";
import { LangContext } from "../../../contexts/Contexts";
import { Dropdown } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import {
  getChatEmojiCatalog,
  readCachedChatEmojis,
} from "../../../../infrastructure/utils/chatEmojiCache";
import { parseReactionMessage } from "../../../../infrastructure/utils/chatReactionPreview";
import { getReactionTargetText } from "../../../../infrastructure/utils/chatReactionPreviewText";

const ContactItem = ({
  avatar,
  name,
  lastMessage,
  timestamp,
  unreadCount = 0,
  isUnread = false,
  isActive = false,
  menuItems = [],
  onMenuClick,
}) => {
  const { lang } = useContext(LangContext);
  const [emojiCatalog, setEmojiCatalog] = useState(() => readCachedChatEmojis());

  const getLocale = (currentLang) => {
    switch (currentLang) {
      case "es":
        return es;
      case "en":
        return enUS;
      default:
        return enUS;
    }
  };

  const date = new Date(timestamp);
  const timeAgo = isValid(date)
    ? formatDistanceToNow(date, {
        addSuffix: true,
        locale: getLocale(lang),
      })
    : "";

  const lastMessagePreview = useMemo(() => {
    const parsedReaction = parseReactionMessage(lastMessage, emojiCatalog);

    if (!parsedReaction) {
      return lastMessage;
    }

    const emojiIcon = parsedReaction.emoji?.Icon?.trim?.() || "";
    const emojiLabel =
      parsedReaction.emoji?.Name ||
      parsedReaction.emoji?.Native ||
      parsedReaction.reactionCode;
    const targetMessage =
      getReactionTargetText(parsedReaction.afterCode) ||
      (lang?.toLowerCase?.().startsWith("es") ? "tu mensaje" : "your message");
    const reactionText = lang?.toLowerCase?.().startsWith("es")
      ? { before: "Reacciono ", after: ` a ${targetMessage}` }
      : { before: "Reacted ", after: ` to ${targetMessage}` };

    return (
      <>
        {reactionText.before}
        {emojiIcon ? (
          <img
            src={emojiIcon}
            alt={emojiLabel}
            style={{ width: "16px", height: "16px", verticalAlign: "text-bottom" }}
          />
        ) : (
          parsedReaction.emoji?.Native || parsedReaction.emoji?.Name || "\u{1F60A}"
        )}
        {reactionText.after}
      </>
    );
  }, [emojiCatalog, lang, lastMessage]);

  useEffect(() => {
    if (emojiCatalog.length) {
      return;
    }

    let isMounted = true;

    getChatEmojiCatalog()
      .then((emojis) => {
        if (isMounted) {
          setEmojiCatalog(emojis || []);
        }
      })
      .catch((error) => {
        console.error("Error loading contact preview emojis:", error);
      });

    return () => {
      isMounted = false;
    };
  }, [emojiCatalog.length]);

  return (
    <div
      className={`contact-item ${isUnread ? "contact-item--unread" : ""} ${isActive ? "contact-item--active" : ""}`}
    >
      <div className="contact-item__avatar-wrap">
        <img
          src={
            name === "Usuario VivaNomada"
              ? "/img/icons/UserFalse.svg"
              : avatar || "/img/icons/UserFalse.svg"
          }
          alt=""
          aria-hidden="true"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = "/img/icons/UserFalse.svg";
          }}
        />
        {isUnread ? <span className="contact-item__status-dot" /> : null}
      </div>

      <div className="contact-info">
        <div className="contact-info-name">
          <strong>{name}</strong>
          <p className="timestamp-contact-item">{timeAgo}</p>
        </div>

        <div className="contact-item__message-row">
          <p className="contact-item__last-message">{lastMessagePreview}</p>
          {unreadCount > 0 ? (
            <span className="contact-item__badge">{unreadCount}</span>
          ) : null}
        </div>
      </div>

      {menuItems.length ? (
        <Dropdown
          menu={{
            items: menuItems,
            onClick: ({ key, domEvent }) => {
              domEvent?.stopPropagation?.();
              onMenuClick?.(key);
            },
          }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <button
            type="button"
            className="contact-item__menu-button"
            aria-label={`Chat actions for ${name}`}
            onClick={(event) => event.stopPropagation()}
          >
            <MoreOutlined />
          </button>
        </Dropdown>
      ) : null}
    </div>
  );
};

export default ContactItem;
